type AttuneErrorPayload = Record<string, unknown>;

type AttuneErrorResponseShape = {
  status: number;
  statusText?: string;
};

function isRecord(value: unknown): value is AttuneErrorPayload {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readMessage(payload: AttuneErrorPayload, fallback: string): string {
  const rawError = payload.error;
  if (typeof rawError === 'string' && rawError.trim()) return rawError.trim();
  if (isRecord(rawError) && typeof rawError.message === 'string' && rawError.message.trim()) {
    return rawError.message.trim();
  }
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message.trim();
  return fallback;
}

function readCode(payload: AttuneErrorPayload): string | null {
  const rawCode = payload.code;
  return typeof rawCode === 'string' && rawCode.trim() ? rawCode.trim() : null;
}

export class AttuneClientRequestError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly code: string | null;
  readonly payload: AttuneErrorPayload;

  constructor(
    message: string,
    options: {
      status: number;
      statusText?: string;
      code?: string | null;
      payload?: AttuneErrorPayload;
    }
  ) {
    super(message);
    this.name = 'AttuneClientRequestError';
    this.status = options.status;
    this.statusText = options.statusText || '';
    this.code = options.code || null;
    this.payload = options.payload || {};
  }
}

export function createAttuneClientRequestError(
  response: AttuneErrorResponseShape,
  payload: unknown,
  fallback = 'Request failed'
): AttuneClientRequestError {
  const payloadRecord = isRecord(payload) ? payload : {};
  return new AttuneClientRequestError(readMessage(payloadRecord, fallback), {
    status: response.status,
    statusText: response.statusText,
    code: readCode(payloadRecord),
    payload: payloadRecord,
  });
}

export function isAttuneClientRequestError(error: unknown): error is AttuneClientRequestError {
  return error instanceof AttuneClientRequestError;
}

export function isOptionalAttunePreloadError(error: unknown): boolean {
  if (!isAttuneClientRequestError(error)) return false;
  return error.status === 403 || error.status === 404 || error.status >= 500;
}
